import { useEffect, useMemo, useState } from "react";
import {
  App,
  Checkbox,
  Drawer,
  Empty,
  Flex,
  Input,
  Menu,
  Space,
  Spin,
  Typography,
} from "antd";
import { Trans, useTranslation } from "react-i18next";
import SearchIcon from "@/assets/search.svg?react";
import { ApiError } from "@/api/client";
import {
  addVersionEvents,
  getEventCatalog,
  type CatalogEventItem,
} from "@/api/events";
import BrandButton from "@/components/BrandButton";
import { useCurrentApp } from "@/hooks/useCurrentApp";
import { useAppStore } from "@/store";
import type { AppVersion, EventSubscribeType } from "@/types";
import styles from "./event.module.scss";
import { PageTabs } from "@/components/Tabs";
import { editableVersionId } from "./editableVersion";

const EMPTY_VERSIONS: AppVersion[] = [];

function errorText(err: unknown, fallback: string) {
  return err instanceof ApiError && err.message ? err.message : fallback;
}

export function AddEventsDrawer({
  open,
  addedEventCodes,
  onClose,
  onAdded,
}: {
  open: boolean;
  addedEventCodes: string[];
  onClose: () => void;
  onAdded: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const app = useCurrentApp();
  const versions = useAppStore((s) => s.versions[app.id] ?? EMPTY_VERSIONS);
  const versionId = editableVersionId(versions);

  const [keyword, setKeyword] = useState("");
  const [subscribeType, setSubscribeType] =
    useState<EventSubscribeType>("tenant");
  const [category, setCategory] = useState("");
  const [menu, setMenu] = useState<{ code: string; name: string }[]>([]);
  const [events, setEvents] = useState<CatalogEventItem[]>([]);
  const [ready, setReady] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const addedCodes = new Set(addedEventCodes);
  const query = keyword.trim().toLowerCase();
  const searching = query.length > 0;
  const visibleEvents = useMemo(() => {
    if (!query) return events.filter((item) => item.categoryCode === category);
    return events.filter((item) => {
      const name = item.name.toLowerCase();
      const code = item.eventCode.toLowerCase();
      const description = item.description.toLowerCase();
      return (
        name.includes(query) ||
        code.includes(query) ||
        description.includes(query)
      );
    });
  }, [events, category, query]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setReady(false);
    getEventCatalog(app.id)
      .then((catalog) => {
        if (cancelled) return;
        const next = catalog.categories.map((item) => ({
          code: item.code,
          name: item.name,
        }));
        setMenu(next);
        setEvents(catalog.categories.flatMap((item) => item.events));
        setCategory((current) =>
          next.some((item) => item.code === current)
            ? current
            : (next[0]?.code ?? ""),
        );
        setReady(true);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setMenu([]);
        setEvents([]);
        setCategory("");
        setReady(true);
        message.error(errorText(err, t("events.loadFailed")));
      });
    return () => {
      cancelled = true;
    };
  }, [open, app.id, i18n.language]);

  const reset = () => {
    setKeyword("");
    setSubscribeType("tenant");
    setCategory("");
    setMenu([]);
    setEvents([]);
    setReady(false);
    setPicked([]);
    setSubmitting(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const toggle = (eventCode: string) => {
    if (addedCodes.has(eventCode)) return;
    setPicked((prev) => {
      if (prev.includes(eventCode))
        return prev.filter((code) => code !== eventCode);
      return [...prev, eventCode];
    });
  };

  const confirm = async () => {
    if (submitting || picked.length === 0) return;
    const eventCodes = picked.filter((code) =>
      events.some((event) => event.eventCode === code),
    );
    if (eventCodes.length === 0) return;
    if (!versionId) {
      message.error(t("events.versionRequired"));
      return;
    }
    setSubmitting(true);
    try {
      await addVersionEvents(app.id, versionId, eventCodes);
      onAdded();
      message.success(t("events.added"));
      close();
    } catch (err) {
      message.error(errorText(err, t("events.addFailed")));
    } finally {
      setSubmitting(false);
    }
  };

  const showSpin = !ready;

  return (
    <Drawer
      title={t("events.addDrawerTitle")}
      open={open}
      onClose={close}
      size={720}
      destroyOnHidden
      footer={
        <Flex align="center" justify="space-between">
          <Typography.Text type="secondary">
            <Trans
              i18nKey="events.selectedCount"
              values={{ count: picked.length }}
              components={{
                count: <span className="font-medium text-primary" />,
              }}
            />
          </Typography.Text>
          <Space>
            <BrandButton variant="outline" onClick={close}>
              {t("common.cancel")}
            </BrandButton>
            <BrandButton
              disabled={picked.length === 0}
              loading={submitting}
              onClick={() => void confirm()}
            >
              {t("events.addEvents")}
            </BrandButton>
          </Space>
        </Flex>
      }
    >
      <Flex vertical gap={16}>
        <Input
          allowClear
          prefix={<SearchIcon className="text-muted" />}
          placeholder={t("events.searchPlaceholder")}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          className="h-10.5"
        />

        <PageTabs
          activeKey={subscribeType}
          onChange={(key) => {
            setSubscribeType(key);
            setPicked([]);
          }}
          items={[
            {
              key: "tenant",
              label: <Space size={4}>{t("events.tenantTab")}</Space>,
            },
            {
              key: "user",
              label: <Space size={4}>{t("events.userTab")}</Space>,
            },
          ]}
        />

        <Flex className="min-h-[360px] border-t border-line !pt-5" gap={16}>
          {!searching ? (
            <Menu
              selectedKeys={category ? [category] : []}
              onClick={({ key }) => setCategory(key)}
              className={styles.menu}
              items={menu.map((item) => ({
                key: item.code,
                label: item.name,
              }))}
            />
          ) : null}

          <Flex vertical flex={1} className="min-w-0 gap-4">
            {showSpin ? (
              <Flex flex={1} align="center" justify="center">
                <Spin />
              </Flex>
            ) : visibleEvents.length === 0 ? (
              <Empty
                description={
                  query ? t("events.searchEmpty") : t("common.empty")
                }
              />
            ) : (
              visibleEvents.map((item) => {
                const already = addedCodes.has(item.eventCode);
                const selected = picked.includes(item.eventCode);
                return (
                  <Checkbox
                    key={item.eventCode}
                    disabled={already}
                    checked={already || selected}
                    onChange={() => toggle(item.eventCode)}
                    className="!items-start py-2.5"
                    styles={{
                      label: {
                        width: "100%",
                      },
                    }}
                  >
                    <Flex
                      vertical
                      gap={2}
                      className="border-b border-line !pb-4"
                    >
                      <Typography.Text>{item.name}</Typography.Text>
                      <Typography.Text type="secondary" className="text-xs">
                        {item.eventCode}
                      </Typography.Text>
                      {item.description ? (
                        <Typography.Text type="secondary" className="text-xs">
                          {item.description}
                        </Typography.Text>
                      ) : null}
                    </Flex>
                  </Checkbox>
                );
              })
            )}
          </Flex>
        </Flex>
      </Flex>
    </Drawer>
  );
}
